<template>
  <div class="map-container">
    <div v-if="false && !isOnline" class="offline-notice">
      You are currently offline. Using cached map data.
    </div>
    <l-map ref="map" v-model:zoom="zoom" :center="center" :min-zoom="15" :max-zoom="dynamicMaxZoom" :max-bounds="mapBounds" :max-bounds-viscosity="1.0">
      <l-marker v-if="userLocation" :lat-lng="userLocation"></l-marker>
      <l-tile-layer
        v-if="!showInset"
        :url="`https://mapling-tiles.bergen.workers.dev/tiles/{z}/{x}/{y}.${tileFormat}`"
        :bounds="[[51.067944, 1.232861], [51.104028, 1.111861]]"
        :opacity="1"
        :max-zoom="19"
        :tile-size="256"
        :zoom-offset="0"
      ></l-tile-layer>
      <l-tile-layer
        v-if="showInset"
        :url="`https://mapling-tiles.bergen.workers.dev/tiles-inset-full/{z}/{x}/{y}.${tileFormat}`"
        :bounds="[[51.085361, 1.177083], [51.076944, 1.189722]]"
        :opacity="1"
        :max-zoom="19"
        :tile-size="256"
        :zoom-offset="0"
      ></l-tile-layer>
    </l-map>
    <button
      v-if="userLocation"
      @click="recenterToUser"
      class="recenter-btn"
      title="Center map on current location"
    >
      📍
    </button>
    <button
      v-if="zoom >= 16 && zoom <= 19"
      @click="toggleInset"
      class="toggle-btn"
      :title="showInset ? 'Hide inset' : 'Show inset'"
    >
      {{ showInset ? '🌙' : '🌞' }}
    </button>
    <button
      v-if="userLocation"
      @click="toggleLocationTracking"
      class="tracking-btn"
      :class="{ active: locationTracking }"
      :title="locationTracking ? 'Stop location tracking' : 'Start location tracking'"
    >
      {{ locationTracking ? '📍' : '👀' }}
    </button>
  </div>
</template>

<script>
import { LMap, LTileLayer, LMarker } from '@vue-leaflet/vue-leaflet'
import L from 'leaflet'
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
})
L.Marker.prototype.options.icon = DefaultIcon

export default {
  name: 'MapComponent',
  components: {
    LMap,
    LTileLayer,
    LMarker
  },
  data () {
    return {
      zoom: 16,
      center: [51.082259, 1.181391],
      userLocation: null,
      mousePosition: null,
      isOnline: navigator.onLine,
      locationWatchId: null,
      hasInitialLocation: false,
      showInset: false,
      locationTracking: false
    }
  },
  computed: {
    mapBounds () {
      // Always return bounds of the largest active map layer
      if (this.showInset) {
        // Inset bounds (smaller area)
        return [[51.076944, 1.177083], [51.085361, 1.189722]]
      } else {
        // Main bounds (larger area)
        return [[51.067944, 1.111861], [51.104028, 1.232861]]
      }
    },
    tileFormat () {
      // Check URL path for format specification
      const path = window.location.pathname
      if (path.includes('/webp')) {
        return 'webp'
      } else if (path.includes('/jpeg') || path.includes('/jpg')) {
        return 'jpg'
      } else {
        return 'png' // default
      }
    },
    dynamicMaxZoom () {
      // Return max zoom based on active tile layer
      return this.showInset ? 19 : 18
    }
  },
  watch: {
    userLocation (newVal) {
      console.log('userLocation changed:', newVal)
    },
    showInset (newVal) {
      // Update bounds when inset is toggled if location tracking is enabled
      if (this.locationTracking) {
        console.log('Inset toggled, updating bounds for location tracking')
      }

      // Handle zoom level changes when switching layers
      if (newVal) {
        // Switching to inset layer (zoom 19 available)
        if (this.zoom > 18) {
          console.log('Switching to inset layer, keeping zoom at', this.zoom)
        }
      } else {
        // Switching to main layer (max zoom 18)
        if (this.zoom > 18) {
          console.log('Switching to main layer, reducing zoom to 18')
          this.zoom = 18
        }
      }
    }
  },
  mounted () {
    window.addEventListener('online', this.updateOnlineStatus)
    window.addEventListener('offline', this.updateOnlineStatus)
    this.getUserLocation()
  },
  beforeUnmount () {
    window.removeEventListener('online', this.updateOnlineStatus)
    window.removeEventListener('offline', this.updateOnlineStatus)
    this.stopLocationTracking()
  },
  methods: {
    updateOnlineStatus () {
      this.isOnline = navigator.onLine
    },
    getUserLocation () {
      console.log('getUserLocation called')
      if ('geolocation' in navigator) {
        console.log('Geolocation API available')
        // Get initial location and center map
        navigator.geolocation.getCurrentPosition(
          (position) => {
            console.log('Geolocation success:', position.coords)
            this.userLocation = [position.coords.latitude, position.coords.longitude]
            this.center = this.userLocation
            this.hasInitialLocation = true
            console.log('Initial user location:', this.userLocation)

            // Start periodic location updates
            this.startLocationTracking()
          },
          (error) => {
            console.log('Location access denied or unavailable:', error.message)
            console.log('Error code:', error.code)
            // Keep default center if location is not available
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // 5 minutes
          }
        )
      } else {
        console.log('Geolocation not supported')
      }
    },
    startLocationTracking () {
      // Watch for location changes every 10 seconds
      this.locationWatchId = navigator.geolocation.watchPosition(
        (position) => {
          this.userLocation = [position.coords.latitude, position.coords.longitude]
          console.log('Updated user location:', this.userLocation)

          // Auto-center if location tracking is enabled
          if (this.locationTracking) {
            this.center = this.userLocation
          }
        },
        (error) => {
          console.log('Location tracking error:', error.message)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 10000 // 10 seconds
        }
      )
    },
    stopLocationTracking () {
      if (this.locationWatchId) {
        navigator.geolocation.clearWatch(this.locationWatchId)
        this.locationWatchId = null
      }
    },
    recenterToUser () {
      if (this.userLocation) {
        this.center = this.userLocation
        console.log('Recentered to user location:', this.userLocation)
      }
    },
    onMouseMove (event) {
      this.mousePosition = event.latlng
    },
    toggleInset () {
      this.showInset = !this.showInset
      console.log('Inset visibility toggled:', this.showInset)
    },
    toggleLocationTracking () {
      this.locationTracking = !this.locationTracking
      if (this.locationTracking) {
        this.startLocationTracking()
      } else {
        this.stopLocationTracking()
      }
      console.log('Location tracking toggled:', this.locationTracking)
    }
  }
}
</script>

<style>
.map-container {
  height: 100vh;
  height: 100dvh;
  width: 100%;
  width: 100vw;
  position: relative;
  z-index: 1;

}
/* Mobile-specific adjustments */
@media (max-width: 768px) {
  .map-container {
    /* Account for mobile browser UI */
    height: 100vh;
    height: 100dvh;
    /* Prevent bottom overflow */
    padding-bottom: env(safe-area-inset-bottom);
    /* Ensure full width */
    width: 100vw;
  }
  /* Adjust button positions for mobile */
  .recenter-btn,
  .toggle-btn,
  .tracking-btn {
    /* Move buttons slightly up on mobile */
    top: 10px;
  }
  .recenter-btn {
    right: 10px;
  }
  .toggle-btn {
    right: 70px;
  }
  .tracking-btn {
    left: 70px;
  }
}
.offline-notice {
  position: fixed;
  top: 0;
  width: 100%;
  background: #f44336;
  color: white;
  text-align: center;
  padding: 8px;
  z-index: 1000;
}
.recenter-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: white;
  border: 2px solid #ccc;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  cursor: pointer;
  font-size: 20px;
  z-index: 1000;
  transition: all 0.2s ease;
}
.recenter-btn:hover {
  background: #f0f0f0;
  transform: scale(1.1);
}
.recenter-btn:active {
  transform: scale(0.95);
}
.toggle-btn {
  position: absolute;
  top: 20px;
  right: 80px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: white;
  border: 2px solid #ccc;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  cursor: pointer;
  font-size: 20px;
  z-index: 1000;
  transition: all 0.2s ease;
}
.toggle-btn:hover {
  background: #f0f0f0;
  transform: scale(1.1);
}
.toggle-btn:active {
  transform: scale(0.95);
}
.tracking-btn {
  position: absolute;
  top: 20px;
  left: 80px; /* Adjust position to be next to toggle button */
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: white;
  border: 2px solid #ccc;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  cursor: pointer;
  font-size: 20px;
  z-index: 1000;
  transition: all 0.2s ease;
}
.tracking-btn:hover {
  background: #f0f0f0;
  transform: scale(1.1);
}
.tracking-btn:active {
  transform: scale(0.95);
}
.tracking-btn.active {
  background-color: #4CAF50; /* Green background for active state */
  color: white;
}
</style>
